import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Button,
  Message,
  Space,
  Radio,
  InputNumber,
  Checkbox,
  DatePicker,
  Card,
  Divider,
  Upload,
  Tag,
  Progress
} from '@arco-design/web-react';
import {
  IconBook,
  IconCalendar,
  IconPlus,
  IconDelete,
  IconClockCircle,
  IconFile,
  IconUpload,
  IconCheck,
  IconClose
} from '@arco-design/web-react/icon';
import { getApiUrl } from '@/config/api';
import { useSaveTeacherDetails, useUploadDocument, useDeleteDocument, useSaveDocumentNotes } from '../hooks/useOnboardingData';
import './TeacherDetailsStep.css';
import ReportProblemButton from '../components/ReportProblemButton';
import './DocumentUploadCard.css';
import DocumentPreviewModal from './DocumentPreviewModal';

const FormItem = Form.Item;
const { Option } = Select;
const RadioGroup = Radio.Group;

// Helper: Calcular el grado académico más alto
const calculateHighestDegree = (degrees) => {
  if (!degrees || degrees.length === 0) return null;

  const degreeHierarchy = {
    'licenciatura': 1,
    'maestria': 2,
    'doctorado': 3
  };

  let highest = null;
  let highestValue = 0;

  degrees.forEach(degree => {
    const value = degreeHierarchy[degree.level] || 0;
    if (value > highestValue) {
      highestValue = value;
      highest = degree.level;
    }
  });

  return highest;
};

const normalizeTimeValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{3,4}$/.test(trimmed)) {
      const digits = trimmed.padStart(4, '0');
      return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    }
    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (match) {
      const hours = match[1].padStart(2, '0');
      const minutes = match[2];
      return `${hours}:${minutes}`;
    }
  }

  if (value && typeof value.format === 'function') {
    return value.format('HH:mm');
  }

  return value;
};

const formatTimeInputValue = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') {
    return normalizeTimeValue(value);
  }
  const trimmed = value.trim();
  if (trimmed.includes(':')) {
    return normalizeTimeValue(trimmed);
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 3) {
    return normalizeTimeValue(digits.slice(0, 4));
  }
  return digits;
};

const isValidTimeValue = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
const START_TIME_PLACEHOLDER = '09:00';
const END_TIME_PLACEHOLDER = '13:00';
const normalizeModalityValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  return [value].filter(Boolean);
};

const requiresCityForModality = (value) => {
  const modalities = normalizeModalityValue(value).map((item) => String(item).toLowerCase());
  return modalities.includes('presencial') || modalities.includes('hibrido');
};

function TeacherDetailsStep({
  onNext,
  onBack,
  initialData,
  documents: initialDocuments,
  requestedChanges,
  section = 'academic',
  previewMode = false,
  freeMode = false
}) {
  const { t } = useTranslation('onboarding');
  const [form] = Form.useForm();

  const saveMutation = useSaveTeacherDetails();
  const draftMutation = useSaveTeacherDetails({ partial: true });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const notesMutation = useSaveDocumentNotes();

  const [degrees, setDegrees] = useState([{ level: '', institution: '', field: '', graduation_year: null }]);
  const [previousInstitutions, setPreviousInstitutions] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([
    { day: '', start_time: '', end_time: '', modality: [], city: '' }
  ]);
  const [isSniMember, setIsSniMember] = useState(false);
  const [documents, setDocuments] = useState({});
  const [notes, setNotes] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [focusedTimeField, setFocusedTimeField] = useState(null);
  const showDraftButton = ['academic', 'professional', 'availability'].includes(section);
  const changeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'teacher_details' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);
  const documentChangeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'documents' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);

  const getFieldProps = (field) => {
    const message = changeMap[field];
    if (!message) return {};
    return {
      validateStatus: 'error',
      help: message
    };
  };

  // Cargar datos iniciales si existen
  useEffect(() => {
    if (initialData) {
      // Cargar campos simples
      form.setFieldsValue({
        years_teaching_experience: initialData.years_teaching_experience || 0,
        is_sni_member: initialData.is_sni_member || false,
        sni_level: initialData.sni_level || undefined,
        sni_expiration_date: initialData.sni_expiration_date || null,
        max_hours_per_week: initialData.max_hours_per_week || undefined,
        linkedin_url: initialData.linkedin_url || '',
        portfolio_url: initialData.portfolio_url || ''
      });

      // Cargar degrees array
      if (initialData.degrees && initialData.degrees.length > 0) {
        setDegrees(initialData.degrees);
      }

      // Cargar previous_institutions array
      if (initialData.previous_institutions && initialData.previous_institutions.length > 0) {
        // Normalizar taught_subjects: convertir string a array si es necesario
        const normalizedInstitutions = initialData.previous_institutions.map(inst => ({
          ...inst,
          taught_subjects: Array.isArray(inst.taught_subjects)
            ? inst.taught_subjects
            : (inst.taught_subjects ? inst.taught_subjects.split(',').map(s => s.trim()).filter(s => s) : [])
        }));
        setPreviousInstitutions(normalizedInstitutions);
      }

      // Cargar availability_schedule array
      if (initialData.availability_schedule && initialData.availability_schedule.length > 0) {
        const normalized = initialData.availability_schedule.map((slot) => ({
          ...slot,
          modality: normalizeModalityValue(slot.modality || slot.preferred_modality || ''),
          city: slot.city || slot.location_city || ''
        }));
        setAvailabilitySlots(normalized);
      }

      setIsSniMember(initialData.is_sni_member || false);
    }
  }, [initialData]);

  useEffect(() => {
    setDocuments(initialDocuments || {});
    setNotes(initialDocuments?.notes || '');
  }, [initialDocuments]);

  const validateAcademic = () => {
    if (degrees.length === 0 || !degrees[0].level) {
      Message.error(t('shared.messages.addDegreeRequired'));
      return false;
    }

    const missingDegree = degrees.find((degree) => (
      !degree.level || !degree.institution || !degree.field || !degree.graduation_year
    ));
    if (missingDegree) {
      Message.error(t('shared.messages.completeDegreeData'));
      return false;
    }

    if (isSniMember && !form.getFieldValue('sni_level')) {
      Message.error(t('steps.teacher.messages.selectSniLevel'));
      return false;
    }

    return true;
  };

  const validateProfessional = (values) => {
    if (values.years_teaching_experience === undefined || values.years_teaching_experience === null) {
      Message.error(t('steps.teacher.messages.teachingExperienceRequired'));
      return false;
    }
    return true;
  };

  const normalizeAvailabilitySlots = () => (
    availabilitySlots.map((slot) => ({
      ...slot,
      modality: normalizeModalityValue(slot.modality),
      start_time: normalizeTimeValue(slot.start_time),
      end_time: normalizeTimeValue(slot.end_time)
    }))
  );

  const validateAvailability = () => {
    if (availabilitySlots.length === 0 || !availabilitySlots[0].day) {
      Message.error(t('steps.teacher.messages.addScheduleRequired'));
      return false;
    }

    const missingModality = availabilitySlots.find(
      slot => normalizeModalityValue(slot.modality).length === 0
    );
    if (missingModality) {
      Message.error(t('steps.teacher.messages.selectModalityRequired'));
      return false;
    }

    const missingCity = availabilitySlots.find(
      slot => requiresCityForModality(slot.modality) && !slot.city
    );
    if (missingCity) {
      Message.error(t('steps.teacher.messages.cityRequired'));
      return false;
    }

    const normalizedSlots = normalizeAvailabilitySlots();
    const invalidTimeSlot = normalizedSlots.find(
      (slot) => !isValidTimeValue(slot.start_time) || !isValidTimeValue(slot.end_time)
    );
    if (invalidTimeSlot) {
      Message.error(t('steps.teacher.messages.timeFormatError'));
      return false;
    }

    return true;
  };

  const buildAcademicPayload = (values) => ({
    highest_degree: calculateHighestDegree(degrees),
    degree_count: degrees.length,
    degrees: degrees,
    is_sni_member: values.is_sni_member || false,
    sni_level: values.is_sni_member ? values.sni_level : null,
    sni_expiration_date: values.is_sni_member && values.sni_expiration_date
      ? values.sni_expiration_date
      : null
  });

  const buildProfessionalPayload = (values) => ({
    years_teaching_experience: values.years_teaching_experience,
    previous_institutions: previousInstitutions.length > 0 ? previousInstitutions : [],
    linkedin_url: values.linkedin_url || null,
    portfolio_url: values.portfolio_url || null
  });

  const buildAvailabilityPayload = (values) => ({
    availability_schedule: normalizeAvailabilitySlots(),
    max_hours_per_week: values.max_hours_per_week
  });

  const buildFullPayload = (values) => {
    const base = initialData || {};
    const isSni = values.is_sni_member ?? base.is_sni_member ?? false;

    return {
      highest_degree: calculateHighestDegree(degrees) || base.highest_degree || null,
      degree_count: degrees.length || base.degree_count || 0,
      degrees: degrees.length ? degrees : base.degrees || [],
      years_teaching_experience: values.years_teaching_experience ?? base.years_teaching_experience ?? 0,
      previous_institutions: previousInstitutions.length ? previousInstitutions : base.previous_institutions || [],
      is_sni_member: isSni,
      sni_level: isSni ? (values.sni_level ?? base.sni_level ?? null) : null,
      sni_expiration_date: isSni ? (values.sni_expiration_date ?? base.sni_expiration_date ?? null) : null,
      availability_schedule: normalizeAvailabilitySlots(),
      max_hours_per_week: values.max_hours_per_week ?? base.max_hours_per_week,
      linkedin_url: values.linkedin_url ?? base.linkedin_url ?? null,
      portfolio_url: values.portfolio_url ?? base.portfolio_url ?? null
    };
  };

  const mutationErrorHandler = (error) => {
    console.error('Error al guardar teacher details:', error);
    if (error.response?.data?.detail) {
      Message.error(error.response.data.detail);
    } else {
      Message.error(t('shared.messages.errorSaving'));
    }
  };

  const handleSubmit = (values) => {
    if (previewMode) {
      if (section === 'academic') {
        if (!validateAcademic()) {
          return;
        }
        Message.success(t('steps.teacher.messages.simulationAcademicValidated'));
        onNext(null);
        return;
      }

      if (section === 'professional') {
        if (!validateProfessional(values)) {
          return;
        }
        Message.success(t('steps.teacher.messages.simulationProfessionalValidated'));
        onNext(null);
        return;
      }

      if (!validateAvailability()) {
        return;
      }
      Message.success(t('steps.teacher.messages.simulationAvailabilityValidated'));
      onNext(null);
      return;
    }

    if (section === 'academic') {
      if (!validateAcademic()) {
        return;
      }

      draftMutation.mutate(buildAcademicPayload(values), {
        onSuccess: () => {
          Message.success(t('steps.teacher.messages.academicSaved'));
          onNext(null);
        },
        onError: mutationErrorHandler
      });
      return;
    }

    if (section === 'professional') {
      if (!validateProfessional(values)) {
        return;
      }

      draftMutation.mutate(buildProfessionalPayload(values), {
        onSuccess: () => {
          saveNotes(notes, { silent: true });
          Message.success(t('steps.teacher.messages.professionalSaved'));
          onNext(null);
        },
        onError: mutationErrorHandler
      });
      return;
    }

    if (!validateAvailability()) {
      return;
    }

    if (degrees.length === 0) {
      Message.error(t('steps.teacher.messages.completeAcademicFirst'));
      return;
    }
    const incompleteDegree = degrees.find((degree) => (
      !degree.level || !degree.institution || !degree.field || !degree.graduation_year
    ));
    if (incompleteDegree) {
      Message.error(t('steps.teacher.messages.completeAcademicFirst'));
      return;
    }

    const baseYears = values.years_teaching_experience ?? initialData?.years_teaching_experience;
    if (baseYears === undefined || baseYears === null) {
      Message.error(t('steps.teacher.messages.completeProfessionalFirst'));
      return;
    }

    const payload = buildFullPayload(values);
    saveMutation.mutate(payload, {
      onSuccess: () => {
        Message.success(t('steps.teacher.messages.availabilitySaved'));
        onNext(null);
      },
      onError: mutationErrorHandler
    });
  };

  const handleSaveDraft = () => {
    if (previewMode) {
      Message.success(t('shared.messages.simulationDraftSaved'));
      return;
    }
    const values = form.getFieldsValue();
    if (section === 'academic') {
      if (!validateAcademic()) {
        return;
      }
      draftMutation.mutate(buildAcademicPayload(values), {
        onSuccess: () => {
          Message.success(t('shared.messages.dataSavedDraft'));
        },
        onError: (error) => {
          console.error('Error al guardar borrador:', error);
          Message.error(t('shared.messages.errorSavingDraft'));
        }
      });
      return;
    }

    if (section === 'professional') {
      if (!validateProfessional(values)) {
        return;
      }
      draftMutation.mutate(buildProfessionalPayload(values), {
        onSuccess: () => {
          saveNotes(notes, { silent: true });
          Message.success(t('shared.messages.dataSavedDraft'));
        },
        onError: (error) => {
          console.error('Error al guardar borrador:', error);
          Message.error(t('shared.messages.errorSavingDraft'));
        }
      });
      return;
    }

    if (!validateAvailability()) {
      return;
    }

    draftMutation.mutate(buildAvailabilityPayload(values), {
      onSuccess: () => {
        Message.success(t('shared.messages.dataSavedDraft'));
      },
      onError: (error) => {
        console.error('Error al guardar borrador:', error);
        Message.error(t('shared.messages.errorSavingDraft'));
      }
    });
  };

  const saveNotes = (value, { silent = false } = {}) => {
    if (previewMode) {
      if (!silent) {
        Message.success(t('shared.messages.simulationNotesSaved'));
      }
      return;
    }
    const normalized = (value || '').trim();
    const currentNotes = (documents?.notes || '').trim();

    if (normalized === currentNotes) {
      return;
    }

    notesMutation.mutate(normalized, {
      onSuccess: () => {
        setDocuments(prev => ({ ...prev, notes: normalized }));
        if (!silent) {
          Message.success(t('shared.messages.notesSaved'));
        }
      },
      onError: (error) => {
        console.error('Error al guardar notas:', error);
        if (!silent) {
          Message.error(t('shared.messages.notesError'));
        }
      }
    });
  };

  const handleUpload = (file, documentKey) => {
    if (previewMode) {
      setDocuments(prev => ({
        ...prev,
        [documentKey]: {
          file_name: file?.name || 'Documento',
          file_url: '',
          file_path: '',
          file_type: file?.type || ''
        }
      }));
      Message.success(t('shared.messages.simulationDocUploaded'));
      return false;
    }

    uploadMutation.mutate({ file, documentType: documentKey }, {
      onSuccess: (response) => {
        const result = response?.data || response;
        const uploadedDocument = result?.document;
        if (!uploadedDocument) {
          Message.error(t('shared.messages.documentNoInfo'));
          return;
        }

        setDocuments(prev => ({
          ...prev,
          [documentKey]: uploadedDocument
        }));
        Message.success(t('shared.messages.documentUploaded'));
      },
      onError: (error) => {
        console.error('Error al subir documento:', error);
        Message.error(t('shared.messages.documentUploadError'));
      }
    });

    return false;
  };

  const handleDelete = (documentKey) => {
    if (previewMode) {
      setDocuments(prev => {
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      Message.success(t('shared.messages.simulationDocDeleted'));
      return;
    }

    deleteMutation.mutate(documentKey, {
      onSuccess: () => {
        setDocuments(prev => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
        Message.success(t('shared.messages.documentDeleted'));
      },
      onError: (error) => {
        console.error('Error al eliminar documento:', error);
        Message.error(t('shared.messages.documentDeleteError'));
      }
    });
  };

  const buildFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    if (fileUrl.startsWith('http')) return fileUrl;
    return `${getApiUrl()}${fileUrl}`;
  };

  const getFileExtension = (fileName = '') => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const renderDocumentThumbnail = (doc) => {
    const fileUrl = buildFileUrl(doc?.file_url || doc?.file_path || '');
    const fileName = doc?.file_name || t('shared.labels.document');
    const extension = getFileExtension(fileName);
    const isPdf = extension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
    const extensionLabel = extension ? extension.toUpperCase() : t('shared.labels.file');

    if (!fileUrl) {
      return (
        <div className="document-thumbnail-fallback">
          <IconFile />
          <span>{t('shared.labels.noPreview')}</span>
        </div>
      );
    }

    if (isImage) {
      return (
        <img
          src={fileUrl}
          alt={fileName}
          className="document-thumbnail-image"
          loading="lazy"
        />
      );
    }

    if (isPdf) {
      return (
        <object data={fileUrl} type="application/pdf" className="document-thumbnail-frame">
          <div className="document-thumbnail-fallback">
            <IconFile />
            <span>PDF</span>
          </div>
        </object>
      );
    }

    return (
      <div className="document-thumbnail-fallback">
        <IconFile />
        <span>{extensionLabel}</span>
      </div>
    );
  };

  const openPreview = (doc, title) => {
    if (!doc) return;
    setPreviewDoc(doc);
    setPreviewTitle(title || doc.file_name || t('shared.labels.document'));
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewDoc(null);
    setPreviewTitle('');
  };

  const renderUploadCard = ({ key, label, description, required, disabled, changeMessage }) => {
    const isUploaded = !!documents[key];
    const isUploading = uploadMutation.isPending && uploadMutation.variables?.documentType === key;
    const cardClassName = `document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`;

    return (
      <Card
        key={key}
        className={cardClassName}
        bordered
        hoverable={!isUploaded && !disabled}
        style={disabled ? { opacity: 0.6, background: '#f7f8fa' } : {}}
      >
        <div className="document-card-header">
          <div className="document-title">
            <IconFile style={{ marginRight: 8 }} />
            <span>{label}</span>
            {required && <Tag color="red" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.required')}</Tag>}
            {!required && !disabled && <Tag color="gray" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.optional')}</Tag>}
            {disabled && <Tag color="gray" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.notApplicable')}</Tag>}
          </div>

          {isUploaded && (
            <Tag color="green" icon={<IconCheck />}>
              {t('shared.labels.uploaded')}
            </Tag>
          )}
        </div>

        {description && (
          <div className="document-description">
            <small>{description}</small>
          </div>
        )}
        {changeMessage && (
          <div className="document-change-note">{changeMessage}</div>
        )}

        {isUploading && (
          <Progress
            percent={50}
            status="normal"
            style={{ marginTop: 12 }}
          />
        )}

        {!isUploaded && !isUploading && !disabled && (
          <Upload
            accept=".pdf,.jpg,.jpeg,.png"
            beforeUpload={(file) => handleUpload(file, key)}
            showUploadList={false}
          >
            <Button
              type="outline"
              icon={<IconUpload />}
              style={{ marginTop: 12, width: '100%' }}
            >
              {t('shared.buttons.selectFile')}
            </Button>
          </Upload>
        )}

        {disabled && (
          <div className="document-disabled">
            <small>{t('shared.labels.documentNotRequired')}</small>
          </div>
        )}

        {isUploaded && (
          <div className="document-actions">
            <div className="document-info">
              <button
                type="button"
                className="document-thumbnail"
                onClick={() => openPreview(documents[key], label)}
                aria-label={t('shared.labels.viewDocument')}
              >
                {renderDocumentThumbnail(documents[key])}
              </button>
              <div className="document-meta">
                <small>{documents[key].file_name}</small>
                <small>{(documents[key].file_size / 1024).toFixed(1)} KB</small>
              </div>
            </div>
            <Button
              type="text"
              status="danger"
              icon={<IconClose />}
              onClick={() => handleDelete(key)}
            >
              {t('shared.labels.delete')}
            </Button>
          </div>
        )}

        <div className="document-help">
          <small>{t('shared.labels.fileFormats')}</small>
        </div>
      </Card>
    );
  };

  // ===== DEGREES MANAGEMENT =====
  const addDegree = () => {
    setDegrees([...degrees, { level: '', institution: '', field: '', graduation_year: null }]);
  };

  const removeDegree = (index) => {
    if (degrees.length > 1) {
      setDegrees(degrees.filter((_, i) => i !== index));
    } else {
      Message.warning(t('steps.teacher.messages.minOneDegree'));
    }
  };

  const updateDegree = (index, field, value) => {
    const updated = [...degrees];
    updated[index][field] = value;
    setDegrees(updated);
  };

  // ===== PREVIOUS INSTITUTIONS MANAGEMENT =====
  const addPreviousInstitution = () => {
    setPreviousInstitutions([...previousInstitutions, { name: '', taught_subjects: [], years: 0 }]);
  };

  const removePreviousInstitution = (index) => {
    setPreviousInstitutions(previousInstitutions.filter((_, i) => i !== index));
  };

  const updatePreviousInstitution = (index, field, value) => {
    const updated = [...previousInstitutions];
    updated[index][field] = value;
    setPreviousInstitutions(updated);
  };

  // Agregar/eliminar materia dentro de una institución
  const addSubjectToInstitution = (instIndex) => {
    const updated = [...previousInstitutions];
    if (!Array.isArray(updated[instIndex].taught_subjects)) {
      updated[instIndex].taught_subjects = [];
    }
    updated[instIndex].taught_subjects.push('');
    setPreviousInstitutions(updated);
  };

  const removeSubjectFromInstitution = (instIndex, subjectIndex) => {
    const updated = [...previousInstitutions];
    updated[instIndex].taught_subjects = updated[instIndex].taught_subjects.filter((_, i) => i !== subjectIndex);
    setPreviousInstitutions(updated);
  };

  const updateSubjectInInstitution = (instIndex, subjectIndex, value) => {
    const updated = [...previousInstitutions];
    updated[instIndex].taught_subjects[subjectIndex] = value;
    setPreviousInstitutions(updated);
  };

  // ===== AVAILABILITY SLOTS MANAGEMENT =====
  const addAvailabilitySlot = () => {
    setAvailabilitySlots([
      ...availabilitySlots,
      { day: '', start_time: '', end_time: '', modality: [], city: '' }
    ]);
  };

  const removeAvailabilitySlot = (index) => {
    if (availabilitySlots.length > 1) {
      setAvailabilitySlots(availabilitySlots.filter((_, i) => i !== index));
    } else {
      Message.warning(t('steps.teacher.messages.minOneSchedule'));
    }
  };

  const updateAvailabilitySlot = (index, field, value) => {
    const updated = [...availabilitySlots];
    if (field === 'start_time' || field === 'end_time') {
      updated[index][field] = formatTimeInputValue(value);
    } else {
      updated[index][field] = value;
    }
    if (field === 'modality' && !requiresCityForModality(value)) {
      updated[index].city = '';
    }
    setAvailabilitySlots(updated);
  };

  const currentYear = new Date().getFullYear();
  const sectionConfig = {
    academic: {
      title: t('steps.teacher.sections.academic.title'),
      description: t('steps.teacher.sections.academic.description')
    },
    professional: {
      title: t('steps.teacher.sections.professional.title'),
      description: t('steps.teacher.sections.professional.description')
    },
    availability: {
      title: t('steps.teacher.sections.availability.title'),
      description: t('steps.teacher.sections.availability.description')
    }
  };
  const showAcademic = section === 'academic';
  const showProfessional = section === 'professional';
  const showAvailability = section === 'availability';

  return (
    <div className="teacher-details-step">
      <div className="step-header">
        <h2>{sectionConfig[section]?.title || t('steps.teacher.defaultTitle')}</h2>
        <p>{sectionConfig[section]?.description || t('steps.teacher.defaultDescription')}</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        {showAcademic && (
          <div className="form-section">
            <h3>
              <IconBook style={{ marginRight: 8 }} />
              {t('shared.sections.academicBackground')}
            </h3>

            <Divider orientation="left">{t('shared.sections.academicDegrees', { count: degrees.length })}</Divider>

            {degrees.map((degree, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16 }}
                title={t('shared.labels.degreeTitle', { number: index + 1 })}
                extra={
                  degrees.length > 1 && (
                    <Button
                      type="text"
                      status="danger"
                      icon={<IconDelete />}
                      onClick={() => removeDegree(index)}
                    >
                      {t('shared.labels.delete')}
                    </Button>
                  )
                }
              >
                <FormItem label={t('shared.labels.level')} required {...getFieldProps(`degrees.${index}.level`)}>
                  <Select
                    placeholder={t('shared.placeholders.degreeLevel')}
                    value={degree.level}
                    onChange={(value) => updateDegree(index, 'level', value)}
                  >
                    <Option value="licenciatura">{t('shared.degrees.licenciatura')}</Option>
                    <Option value="maestria">{t('shared.degrees.maestria')}</Option>
                    <Option value="doctorado">{t('shared.degrees.doctorado')}</Option>
                  </Select>
                </FormItem>

                <FormItem label={t('shared.labels.institution')} required {...getFieldProps(`degrees.${index}.institution`)}>
                  <Input
                    placeholder={t('shared.placeholders.institution')}
                    value={degree.institution}
                    onChange={(value) => updateDegree(index, 'institution', value)}
                  />
                </FormItem>

                <FormItem label={t('shared.labels.degree')} required {...getFieldProps(`degrees.${index}.field`)}>
                  <Input
                    placeholder={t('shared.placeholders.field')}
                    value={degree.field}
                    onChange={(value) => updateDegree(index, 'field', value)}
                  />
                </FormItem>

                <FormItem label={t('shared.labels.graduationYear')} required {...getFieldProps(`degrees.${index}.graduation_year`)}>
                  <InputNumber
                    placeholder={t('shared.placeholders.year2015')}
                    min={1950}
                    max={currentYear}
                    value={degree.graduation_year}
                    onChange={(value) => updateDegree(index, 'graduation_year', value)}
                    style={{ width: '100%' }}
                  />
                </FormItem>

                <Divider orientation="left">{t('shared.documents.degreeDocTitle')}</Divider>
                {renderUploadCard({
                  key: `degree_${index}`,
                  label: t('shared.documents.degreeDocLabel', { number: index + 1 }),
                  description: `${degree.level ? degree.level.charAt(0).toUpperCase() + degree.level.slice(1) : t('shared.labels.degreeGeneric')}${degree.field ? ` - ${degree.field}` : ''}${degree.institution ? ` (${degree.institution})` : ''}`.trim(),
                  required: true,
                  changeMessage: documentChangeMap[`degree_${index}`]
                })}
              </Card>
            ))}

            <Button
              type="dashed"
              long
              icon={<IconPlus />}
              onClick={addDegree}
            >
              {t('shared.buttons.addDegree')}
            </Button>

            <Divider orientation="left">{t('shared.documents.professionalLicenseTitle')}</Divider>
            {renderUploadCard({
              key: 'professional_license',
              label: t('shared.documents.professionalLicenseTitle'),
              description: t('shared.documents.professionalLicenseDesc'),
              required: false,
              changeMessage: documentChangeMap.professional_license
            })}
          </div>
        )}

        {showProfessional && (
          <div className="form-section">
            <h3>
              <IconCalendar style={{ marginRight: 8 }} />
              {t('steps.teacher.labels.teachingExperienceDocente')}
            </h3>

            <FormItem
              label={t('steps.teacher.labels.teachingExperience')}
              field="years_teaching_experience"
              rules={[
                { required: true, message: t('steps.teacher.messages.teachingExperienceRule') },
                { type: 'number', min: 0, max: 50 }
              ]}
              {...getFieldProps('years_teaching_experience')}
            >
              <InputNumber
                placeholder={t('shared.placeholders.experience')}
                min={0}
                max={50}
                style={{ width: '100%' }}
              />
            </FormItem>

            <Divider orientation="left">{t('steps.teacher.labels.previousInstitutions')}</Divider>

            {previousInstitutions.map((inst, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16 }}
                title={t('shared.labels.institutionTitle', { number: index + 1 })}
                extra={
                  <Button
                    type="text"
                    status="danger"
                    icon={<IconDelete />}
                    onClick={() => removePreviousInstitution(index)}
                  >
                    {t('shared.labels.delete')}
                  </Button>
                }
              >
                <FormItem
                  label={t('steps.teacher.labels.institutionName')}
                  required
                  {...getFieldProps(`previous_institutions.${index}.name`)}
                >
                  <Input
                    placeholder={t('steps.teacher.placeholders.institutionName')}
                    value={inst.name}
                    onChange={(value) => updatePreviousInstitution(index, 'name', value)}
                  />
                </FormItem>

                <FormItem
                  label={t('steps.teacher.labels.taughtSubjects')}
                  required
                  {...getFieldProps(`previous_institutions.${index}.taught_subjects`)}
                >
                  <div>
                    {Array.isArray(inst.taught_subjects) && inst.taught_subjects.length > 0 ? (
                      inst.taught_subjects.map((subject, subjectIndex) => (
                        <Space key={subjectIndex} style={{ marginBottom: 8, width: '100%' }} size="small">
                          <Input
                            placeholder={t('steps.teacher.placeholders.subject')}
                            value={subject}
                            onChange={(value) => updateSubjectInInstitution(index, subjectIndex, value)}
                            style={{ flex: 1, minWidth: 300 }}
                          />
                          <Button
                            type="text"
                            status="danger"
                            icon={<IconDelete />}
                            onClick={() => removeSubjectFromInstitution(index, subjectIndex)}
                          >
                            {t('shared.labels.delete')}
                          </Button>
                        </Space>
                      ))
                    ) : (
                      <p style={{ color: '#86909c', marginBottom: 8 }}>{t('steps.teacher.labels.noSubjects')}</p>
                    )}
                    <Button
                      type="dashed"
                      icon={<IconPlus />}
                      onClick={() => addSubjectToInstitution(index)}
                      style={{ marginTop: 8 }}
                    >
                      {t('shared.buttons.addSubject')}
                    </Button>
                  </div>
                </FormItem>

                <FormItem
                  label={t('steps.teacher.labels.years')}
                  required
                  {...getFieldProps(`previous_institutions.${index}.years`)}
                >
                  <InputNumber
                    placeholder={t('shared.placeholders.years')}
                    min={0}
                    max={50}
                    value={inst.years}
                    onChange={(value) => updatePreviousInstitution(index, 'years', value)}
                    style={{ width: '100%' }}
                  />
                </FormItem>
              </Card>
            ))}

            <Button
              type="dashed"
              long
              icon={<IconPlus />}
              onClick={addPreviousInstitution}
            >
              {t('shared.buttons.addInstitution')}
            </Button>
          </div>
        )}

        {showAcademic && (
          <div className="form-section">
            <h3>
              <IconBook style={{ marginRight: 8 }} />
              {t('steps.teacher.labels.sniTitle')}
            </h3>

            <FormItem field="is_sni_member" {...getFieldProps('is_sni_member')}>
              <Checkbox
                checked={isSniMember}
                onChange={(checked) => {
                  setIsSniMember(checked);
                  form.setFieldValue('is_sni_member', checked);
                  if (!checked) {
                    form.setFieldValue('sni_level', undefined);
                    form.setFieldValue('sni_expiration_date', null);
                  }
                }}
              >
                {t('steps.teacher.labels.sniMember')}
              </Checkbox>
            </FormItem>

            {isSniMember && (
              <>
                <FormItem
                  label={t('steps.teacher.labels.sniLevel')}
                  field="sni_level"
                  rules={[{ required: isSniMember, message: t('steps.teacher.messages.selectSniLevel') }]}
                  {...getFieldProps('sni_level')}
                >
                  <Select placeholder={t('steps.teacher.placeholders.sniLevel')}>
                    <Option value="Candidato">{t('steps.teacher.options.candidato')}</Option>
                    <Option value="Nivel I">{t('steps.teacher.options.nivelI')}</Option>
                    <Option value="Nivel II">{t('steps.teacher.options.nivelII')}</Option>
                    <Option value="Nivel III">{t('steps.teacher.options.nivelIII')}</Option>
                    <Option value="Emérito">{t('steps.teacher.options.emerito')}</Option>
                  </Select>
                </FormItem>

                <FormItem
                  label={t('steps.teacher.labels.sniExpiration')}
                  field="sni_expiration_date"
                  {...getFieldProps('sni_expiration_date')}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder={t('steps.teacher.placeholders.sniExpiration')}
                    format="YYYY-MM-DD"
                  />
                </FormItem>

                <Divider orientation="left">{t('steps.teacher.labels.sniCertificateTitle')}</Divider>
                {renderUploadCard({
                  key: 'sni_certificate',
                  label: t('steps.teacher.labels.sniCertificateTitle'),
                  description: t('steps.teacher.labels.sniCertificateDesc'),
                  required: true,
                  changeMessage: documentChangeMap.sni_certificate
                })}
              </>
            )}
          </div>
        )}

        {showAvailability && (
          <div className="form-section">
            <h3>
              <IconClockCircle style={{ marginRight: 8 }} />
              {t('status.sections.availability')}
            </h3>

            <Divider orientation="left">{t('steps.teacher.labels.availableSchedules', { count: availabilitySlots.length })}</Divider>

            {availabilitySlots.map((slot, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16 }}
                title={t('steps.teacher.labels.scheduleTitle', { number: index + 1 })}
                extra={
                  availabilitySlots.length > 1 && (
                    <Button
                      type="text"
                      status="danger"
                      icon={<IconDelete />}
                      onClick={() => removeAvailabilitySlot(index)}
                    >
                      {t('shared.labels.delete')}
                    </Button>
                  )
                }
              >
                <FormItem label={t('steps.teacher.labels.day')} required {...getFieldProps(`availability_schedule.${index}.day`)}>
                  <Select
                    placeholder={t('steps.teacher.placeholders.selectDay')}
                    value={slot.day}
                    onChange={(value) => updateAvailabilitySlot(index, 'day', value)}
                  >
                    <Option value="Lunes">{t('steps.teacher.options.lunes')}</Option>
                    <Option value="Martes">{t('steps.teacher.options.martes')}</Option>
                    <Option value="Miércoles">{t('steps.teacher.options.miercoles')}</Option>
                    <Option value="Jueves">{t('steps.teacher.options.jueves')}</Option>
                    <Option value="Viernes">{t('steps.teacher.options.viernes')}</Option>
                    <Option value="Sábado">{t('steps.teacher.options.sabado')}</Option>
                    <Option value="Domingo">{t('steps.teacher.options.domingo')}</Option>
                  </Select>
                </FormItem>

                <div className="form-row">
                  <FormItem label={t('steps.teacher.labels.startTime')} required {...getFieldProps(`availability_schedule.${index}.start_time`)}>
                    <Input
                      placeholder={focusedTimeField === `start-${index}` ? '' : 'HH:mm'}
                      className="availability-time-input"
                      value={slot.start_time}
                      onChange={(value) => updateAvailabilitySlot(index, 'start_time', value)}
                      onFocus={() => {
                        setFocusedTimeField(`start-${index}`);
                        if (slot.start_time === START_TIME_PLACEHOLDER) {
                          updateAvailabilitySlot(index, 'start_time', '');
                        }
                      }}
                      onBlur={() => {
                        setFocusedTimeField(null);
                        const formatted = normalizeTimeValue(slot.start_time);
                        if (formatted !== slot.start_time) {
                          updateAvailabilitySlot(index, 'start_time', formatted);
                        }
                      }}
                    />
                  </FormItem>

                  <FormItem label={t('steps.teacher.labels.endTime')} required {...getFieldProps(`availability_schedule.${index}.end_time`)}>
                    <Input
                      placeholder={focusedTimeField === `end-${index}` ? '' : 'HH:mm'}
                      className="availability-time-input"
                      value={slot.end_time}
                      onChange={(value) => updateAvailabilitySlot(index, 'end_time', value)}
                      onFocus={() => {
                        setFocusedTimeField(`end-${index}`);
                        if (slot.end_time === END_TIME_PLACEHOLDER) {
                          updateAvailabilitySlot(index, 'end_time', '');
                        }
                      }}
                      onBlur={() => {
                        setFocusedTimeField(null);
                        const formatted = normalizeTimeValue(slot.end_time);
                        if (formatted !== slot.end_time) {
                          updateAvailabilitySlot(index, 'end_time', formatted);
                        }
                      }}
                    />
                  </FormItem>
                </div>

                <div className="form-row">
                  <FormItem label={t('steps.teacher.labels.modality')} required {...getFieldProps(`availability_schedule.${index}.modality`)}>
                    <Select
                      mode="multiple"
                      placeholder={t('steps.teacher.placeholders.selectModality')}
                      value={slot.modality}
                      onChange={(value) => updateAvailabilitySlot(index, 'modality', value)}
                    >
                      <Option value="virtual">{t('steps.teacher.options.virtual')}</Option>
                      <Option value="presencial">{t('steps.teacher.options.presencial')}</Option>
                      <Option value="hibrido">{t('steps.teacher.options.hibrido')}</Option>
                    </Select>
                  </FormItem>

                  {requiresCityForModality(slot.modality) && (
                    <FormItem label={t('steps.teacher.labels.city')} required {...getFieldProps(`availability_schedule.${index}.city`)}>
                      <Input
                        placeholder={t('steps.teacher.placeholders.city')}
                        value={slot.city}
                        onChange={(value) => updateAvailabilitySlot(index, 'city', value)}
                      />
                    </FormItem>
                  )}
                </div>
              </Card>
            ))}

            <Button
              type="dashed"
              long
              icon={<IconPlus />}
              onClick={addAvailabilitySlot}
            >
              {t('shared.buttons.addSchedule')}
            </Button>

            <FormItem
              label={t('steps.teacher.labels.maxHoursPerWeek')}
              field="max_hours_per_week"
              rules={[
                { required: true, message: t('steps.teacher.messages.maxHoursRule') },
                { type: 'number', min: 1, max: 60 }
              ]}
              style={{ marginTop: 24 }}
              {...getFieldProps('max_hours_per_week')}
            >
              <InputNumber
                placeholder={t('shared.placeholders.maxHours')}
                min={1}
                max={60}
                style={{ width: '100%' }}
              />
            </FormItem>
          </div>
        )}

        {showProfessional && (
          <>
            <div className="form-section">
              <h3>
                <IconFile style={{ marginRight: 8 }} />
                {t('shared.sections.professionalDocumentation')}
              </h3>
              {renderUploadCard({
                key: 'cv',
                label: t('shared.documents.cvLabel'),
                description: t('shared.documents.cvDesc'),
                required: true,
                changeMessage: documentChangeMap.cv
              })}
            </div>

            <div className="form-section">
              <h3>
                <IconBook style={{ marginRight: 8 }} />
                {t('shared.sections.additionalInfoOptional')}
              </h3>

              <FormItem
                label={t('steps.teacher.labels.linkedinUrl')}
                field="linkedin_url"
                rules={[
                  {
                    match: /^https?:\/\/(www\.)?linkedin\.com\/.+$/,
                    message: t('shared.validation.linkedinUrl')
                  }
                ]}
                {...getFieldProps('linkedin_url')}
              >
                <Input placeholder={t('steps.teacher.placeholders.linkedin')} />
              </FormItem>

              <FormItem
                label={t('steps.teacher.labels.portfolioUrl')}
                field="portfolio_url"
                rules={[
                  {
                    match: /^https?:\/\/.+$/,
                    message: t('shared.validation.validUrl')
                  }
                ]}
                {...getFieldProps('portfolio_url')}
              >
                <Input placeholder={t('steps.teacher.placeholders.portfolio')} />
              </FormItem>
            </div>

            <div className="form-section">
              <h3>
                <IconFile style={{ marginRight: 8 }} />
                {t('shared.sections.hiringNotes')}
              </h3>
              <FormItem>
                <Input.TextArea
                  value={notes}
                  onChange={setNotes}
                  onBlur={() => saveNotes(notes)}
                  placeholder={t('steps.teacher.placeholders.hiringNotes')}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                />
              </FormItem>
              <small style={{ color: 'var(--color-text-3)' }}>
                {t('shared.sections.hiringNotesHint')}
              </small>
            </div>
          </>
        )}

        {/* ========== BOTONES ========== */}
        <div className="form-actions">
          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button size="large" onClick={onBack}>
              {t('shared.buttons.back')}
            </Button>
            {showAcademic && (
              <Button
                size="large"
                onClick={handleSaveDraft}
                loading={draftMutation.isPending}
                className="onboarding-draft-button"
              >
                {t('shared.buttons.saveDraft')}
              </Button>
            )}
            {!showAcademic && showDraftButton && (
              <Button
                size="large"
                onClick={handleSaveDraft}
                loading={draftMutation.isPending}
                className="onboarding-draft-button"
              >
                {t('shared.buttons.saveDraft')}
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={saveMutation.isPending || draftMutation.isPending}
              disabled={notesMutation.isPending}
            >
              {t('shared.buttons.continue')}
            </Button>
            {previewMode && freeMode && (
              <Button
                size="large"
                onClick={() => onNext(null)}
              >
                {t('shared.buttons.skipValidation')}
              </Button>
            )}
            <ReportProblemButton stepLabel={sectionConfig[section]?.title || t('steps.teacher.defaultTitle')} previewMode={previewMode} asButton />
          </Space>
        </div>
      </Form>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={closePreview}
        document={previewDoc}
        title={previewTitle}
      />
    </div>
  );
}

export default TeacherDetailsStep;
